// Modal primitive — 시안엔 명시 모달 없음 (시안은 inline form).
// 직접 구현: backdrop click + ESC 닫기 + simple focus trap (first/last wrap) + body scroll lock.
// 토큰: tokens.css 만 참조.

import {
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { Icon } from './icons';

export type ModalSize = 'sm' | 'md' | 'lg';

const widths: Record<ModalSize, number> = {
  sm: 360,
  md: 520,
  lg: 760,
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  style?: CSSProperties;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  style,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // body scroll lock + ESC handler
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);

    // Initial focus to dialog
    requestAnimationFrame(() => {
      dialogRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleBackdrop(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  // Simple focus trap: when Tab from last focusable wraps to first, Shift+Tab from first wraps to last.
  function handleKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(28, 25, 23, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--modal-outer-pad)',
        zIndex: 1000,
        animation: 'dvvb-modal-fade var(--dur-base) var(--ease-out)',
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleKey}
        style={{
          width: '100%',
          maxWidth: widths[size],
          maxHeight: 'var(--modal-max-h)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
          minWidth: 0,
          minHeight: 0,
          boxSizing: 'border-box',
          ...style,
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              padding: 'var(--modal-header-pad)',
              borderBottom: '1px solid var(--border-weak)',
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 700,
                color: 'var(--fg-1)',
                letterSpacing: 0,
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </div>
            <button
              type="button"
              aria-label="닫기"
              onClick={onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                border: '1px solid transparent',
                color: 'var(--fg-3)',
                cursor: 'pointer',
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
        )}
        <div
          style={{
            padding: 'var(--modal-body-pad)',
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            fontSize: 13,
            color: 'var(--fg-1)',
            lineHeight: 'var(--lh-body)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: 'var(--modal-footer-pad)',
              borderTop: '1px solid var(--border-weak)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
      <style>{`
        @keyframes dvvb-modal-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
